import { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Loader2, AlertTriangle } from "lucide-react";

/**
 * Manage commission criteria for a specific seller.
 * Props:
 *  - open, onOpenChange
 *  - seller: { id, name }
 *  - allSellers: full list of sellers (for copy-from)
 */
export const SellerCriteriaDialog = ({ open, onOpenChange, seller, allSellers = [] }) => {
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | criterionId
  const [form, setForm] = useState({ label: "", weight: 0, order_index: 0 });
  const [copyFromId, setCopyFromId] = useState("");

  const load = async () => {
    if (!seller?.id) return;
    try {
      setLoading(true);
      const res = await api.getSellerCommissionCriteria(seller.id);
      setCriteria(res.data.criteria || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && seller?.id) {
      load();
      setEditing(null);
      setCopyFromId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seller?.id]);

  const totalWeight = criteria.reduce((s, c) => s + (c.weight || 0), 0);

  const startNew = () => {
    setForm({ label: "", weight: Math.max(0, 100 - totalWeight), order_index: criteria.length + 1 });
    setEditing("new");
  };

  const startEdit = (c) => {
    setForm({ label: c.label, weight: c.weight, order_index: c.order_index });
    setEditing(c.id);
  };

  const handleSave = async () => {
    if (!form.label.trim()) { toast.error("Libellé requis"); return; }
    if (form.weight < 0 || form.weight > 100) { toast.error("Poids entre 0 et 100"); return; }
    try {
      setSaving(true);
      if (editing === "new") {
        await api.createSellerCommissionCriterion(seller.id, {
          label: form.label.trim(),
          weight: Number(form.weight),
          order_index: Number(form.order_index) || 0,
        });
        toast.success("Critère ajouté");
      } else {
        await api.updateSellerCommissionCriterion(seller.id, editing, {
          label: form.label.trim(),
          weight: Number(form.weight),
          order_index: Number(form.order_index) || 0,
        });
        toast.success("Critère mis à jour");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer le critère "${c.label}" ?`)) return;
    try {
      await api.deleteSellerCommissionCriterion(seller.id, c.id);
      toast.success("Critère supprimé");
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  const handleCopy = async () => {
    if (!copyFromId) { toast.error("Sélectionnez un vendeur source"); return; }
    try {
      const res = await api.copySellerCommissionCriteria(seller.id, copyFromId);
      toast.success(res.data.message);
      setCopyFromId("");
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de copie"));
    }
  };

  const otherSellers = allSellers.filter((s) => s.id !== seller?.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="seller-criteria-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Critères de commission — {seller?.name}</DialogTitle>
          <DialogDescription>
            Définissez les critères (suivi chantier, prix, délais, ...) avec leur poids. La somme des poids doit valoir 100%.
            Le ratio cochés / total sera appliqué à la commission de chaque commande terminée.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge
                variant="outline"
                className={Math.abs(totalWeight - 100) < 0.01 ? "border-green-400 text-green-700" : "border-amber-400 text-amber-700"}
              >
                Somme des poids : {totalWeight}% / 100%
              </Badge>
              {editing === null && (
                <Button size="sm" onClick={startNew} data-testid="add-criterion-btn">
                  <Plus className="h-4 w-4 mr-1" /> Ajouter un critère
                </Button>
              )}
            </div>

            {Math.abs(totalWeight - 100) > 0.01 && criteria.length > 0 && (
              <div className="text-xs text-amber-700 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Somme différente de 100%. Le calcul utilisera le ratio poids_cochés / {totalWeight}.
              </div>
            )}

            {criteria.length === 0 && editing === null && (
              <div className="text-sm text-muted-foreground p-4 text-center border rounded-sm">
                Aucun critère défini. Ajoutez-en ou copiez ceux d'un autre vendeur.
              </div>
            )}

            <div className="space-y-2">
              {criteria.map((c) => (
                editing === c.id ? (
                  <CriterionForm
                    key={c.id}
                    form={form}
                    setForm={setForm}
                    onSave={handleSave}
                    onCancel={() => setEditing(null)}
                    saving={saving}
                  />
                ) : (
                  <div key={c.id} className="flex items-center gap-2 p-3 rounded-sm border" data-testid={`criterion-${c.id}`}>
                    <span className="text-xs text-muted-foreground w-6">{c.order_index}.</span>
                    <span className="flex-1">{c.label}</span>
                    <Badge variant="outline">{c.weight}%</Badge>
                    <Button variant="ghost" size="icon" onClick={() => startEdit(c)} data-testid={`edit-criterion-${c.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(c)} data-testid={`delete-criterion-${c.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                )
              ))}
              {editing === "new" && (
                <CriterionForm
                  form={form}
                  setForm={setForm}
                  onSave={handleSave}
                  onCancel={() => setEditing(null)}
                  saving={saving}
                />
              )}
            </div>

            {otherSellers.length > 0 && (
              <div className="p-3 rounded-sm border bg-muted/30 space-y-2">
                <Label className="text-sm">Copier depuis un autre vendeur</Label>
                <div className="flex gap-2">
                  <Select value={copyFromId} onValueChange={setCopyFromId}>
                    <SelectTrigger className="flex-1" data-testid="copy-from-select">
                      <SelectValue placeholder="Choisir un vendeur..." />
                    </SelectTrigger>
                    <SelectContent>
                      {otherSellers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={handleCopy} disabled={!copyFromId} data-testid="copy-criteria-btn">
                    <Copy className="h-4 w-4 mr-1" /> Copier
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CriterionForm = ({ form, setForm, onSave, onCancel, saving }) => (
  <div className="p-3 rounded-sm border bg-primary/5 space-y-2">
    <div className="grid grid-cols-12 gap-2">
      <div className="col-span-2">
        <Label className="text-xs">Ordre</Label>
        <Input
          type="number"
          min="0"
          value={form.order_index}
          onChange={(e) => setForm({ ...form, order_index: e.target.value })}
          data-testid="criterion-order-input"
        />
      </div>
      <div className="col-span-7">
        <Label className="text-xs">Libellé</Label>
        <Input
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="Ex: Suivi chantier"
          data-testid="criterion-label-input"
        />
      </div>
      <div className="col-span-3">
        <Label className="text-xs">Poids (%)</Label>
        <Input
          type="number"
          min="0"
          max="100"
          step="1"
          value={form.weight}
          onChange={(e) => setForm({ ...form, weight: e.target.value })}
          data-testid="criterion-weight-input"
        />
      </div>
    </div>
    <div className="flex justify-end gap-2">
      <Button variant="ghost" size="sm" onClick={onCancel}>Annuler</Button>
      <Button size="sm" onClick={onSave} disabled={saving} data-testid="save-criterion-btn">
        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
        Enregistrer
      </Button>
    </div>
  </div>
);

export default SellerCriteriaDialog;
